/**
 * Konfigurator "Meine Saidi Box".
 *
 * Ablauf: Boxgröße wählen -> Zutaten in 100-g-Schritten hinzufügen ->
 * bei erreichtem Zielgewicht in den Warenkorb.
 *
 * Warenkorb: Jede Zutat wird eine eigene Position mit ihrer 100-g-Variante,
 * Menge = Anzahl der Schritte. Alle Positionen einer Box teilen dieselbe
 * Box-ID in den Zeileneigenschaften, damit später nachvollziehbar bleibt,
 * was zu welcher Box gehörte.
 */
class SaidiBox {
  constructor(wurzel) {
    this.wurzel = wurzel;
    this.schritt = parseInt(wurzel.dataset.schritt, 10) || 100;
    this.ziel = 0;
    this.auswahl = new Map(); // variantId -> { zutat, schritte }

    const rohdaten = wurzel.querySelector('[data-zutaten]');
    try {
      this.zutaten = JSON.parse(rohdaten.textContent);
    } catch (e) {
      // Stiller Ausfall hat hier schon einmal Zeit gekostet: die Box stand
      // leer da, ohne dass irgendwo stand, warum. Jetzt sagt die Konsole es.
      console.error('Saidi Box: Zutatenliste unlesbar.', e);
      this.zutaten = [];
    }
    this.nachgeladen = false;

    this.el = {
      regler: wurzel.querySelector('[data-groesse]'),
      groesseWert: wurzel.querySelector('[data-groesse-wert]'),
      spruenge: wurzel.querySelectorAll('.saidi-regler__sprung [data-gramm]'),
      budgetStufen: wurzel.querySelectorAll('[data-stufe-budget]'),
      stufeZutaten: wurzel.querySelector('[data-stufe="zutaten"]'),
      filter: wurzel.querySelector('.saidi-box__filter'),
      liste: wurzel.querySelector('[data-liste]'),
      leer: wurzel.querySelector('[data-leer]'),
      leiste: wurzel.querySelector('[data-leiste]'),
      ist: wurzel.querySelector('[data-ist]'),
      zielAnzeige: wurzel.querySelector('[data-ziel]'),
      preis: wurzel.querySelector('[data-preis]'),
      fuell: wurzel.querySelector('[data-fuell]'),
      status: wurzel.querySelector('[data-status]'),
      absenden: wurzel.querySelector('[data-absenden]'),
      stufeZufall: wurzel.querySelector('[data-stufe="zufall"]'),
      zufallWahl: wurzel.querySelectorAll('.saidi-zufall__wahl'),
      wuerfeln: wurzel.querySelector('[data-wuerfeln]'),
      zufallHinweis: wurzel.querySelector('[data-zufall-hinweis]'),
      stufeGeschenk: wurzel.querySelector('[data-stufe="geschenk"]'),
      geschenkAn: wurzel.querySelector('[data-geschenk-an]'),
      geschenkDetails: wurzel.querySelector('[data-geschenk-details]'),
      anlaesse: wurzel.querySelector('.saidi-geschenk__anlaesse'),
      nachricht: wurzel.querySelector('[data-nachricht]'),
      zeichen: wurzel.querySelector('[data-zeichen]'),
      von: wurzel.querySelector('[data-von]'),
    };

    // Verpackung kommt aus dem Markup, nicht aus einer Konstante hier:
    // Preis und Variante werden im Admin gepflegt.
    const g = this.el.stufeGeschenk;
    this.geschenkVariante = g ? Number(g.dataset.geschenkId) || 0 : 0;
    this.geschenkPreis = g ? parseInt(g.dataset.geschenkPreis, 10) || 0 : 0;

    this.baueFilter();
    this.verdrahte();

    // Liquid gibt in Sektionen nur 50 Produkte heraus. Fehlt etwas, holt
    // der Konfigurator den Rest selbst nach.
    const gesamt = parseInt(this.wurzel.dataset.gesamt, 10) || 0;
    if (this.zutaten.length < gesamt || !this.zutaten.length) this.nachladen();

    // Der Regler steht von Anfang an auf der kleinsten Box. Fruehere Fassungen
    // warteten auf einen Klick, sonst blieb der halbe Konfigurator verborgen.
    if (this.el.regler) this.setzeGroesse(parseInt(this.el.regler.value, 10), true);

    this.vorauswahlAusAdresse();
  }

  /**
   * Uebernimmt eine Vorauswahl aus dem Merkzettel.
   *
   * Der Merkzettel verlinkt hierher mit ?merken=handle1,handle2. Wer sich
   * sechs Sorten gemerkt hat, will meistens keine sechs Tueten, sondern
   * eine Box - dieser Weg spart ihm, alles noch einmal zusammenzusuchen.
   *
   * Gefuellt wird reihum je eine Portion, nicht dreimal dieselbe Sorte:
   * bei fuenf gemerkten Sorten und einer 500-g-Box bekommt jede 100 g.
   * Passt nicht alles hinein, bleibt der Rest weg - lieber weniger als
   * eine Box, die stillschweigend etwas anderes enthaelt als erwartet.
   */
  vorauswahlAusAdresse() {
    let roh;
    try { roh = new URLSearchParams(window.location.search).get('merken'); }
    catch (e) { return; }
    if (!roh) return;

    const handles = roh.split(',').map((h) => decodeURIComponent(h.trim())).filter(Boolean);
    if (!handles.length) return;

    // Nach dem Nachladen noch einmal versuchen: beim ersten Aufbau liegen
    // hoechstens 50 Zutaten vor, die gemerkte kann darunter fehlen.
    const gefunden = handles
      .map((h) => this.zutaten.find((z) => z.handle === h))
      .filter(Boolean);
    if (!gefunden.length) return;

    // Reicht die eingestellte Boxgroesse fuer alle gemerkten Sorten?
    //
    // Vorher nicht: Der Regler steht beim Aufbau immer auf der kleinsten Box.
    // In 500 g passen fuenf 100-g-Portionen. Wer sich sieben Sorten gemerkt
    // hatte, bekam also fuenf davon - und darueber den Satz "Aus deinem
    // Merkzettel uebernommen", der das Gegenteil behauptete. Genau das
    // "stillschweigend etwas anderes als erwartet", das hier vermieden
    // werden sollte.
    //
    // Jetzt geht die Box auf die naechste angebotene Groesse hoch, in die
    // alle Sorten hineinpassen. Reicht auch die groesste nicht, bleibt es
    // beim Kuerzen - dann sagt es der Hinweis unten aber ausdruecklich.
    const noetig = gefunden.length * this.schritt;
    let vergroessertAuf = 0;
    if (noetig > this.ziel) {
      const groessen = [...this.el.spruenge]
        .map((b) => parseInt(b.dataset.gramm, 10))
        .filter(Boolean);
      const max = this.el.regler ? parseInt(this.el.regler.max, 10) || 0 : 0;
      if (max && groessen.indexOf(max) === -1) groessen.push(max);
      groessen.sort((a, b) => a - b);
      const passend = groessen.filter((g) => g >= noetig)[0];
      if (passend && passend > this.ziel) {
        this.setzeGroesse(passend, true);
        vergroessertAuf = passend;
      }
    }

    const schritte = Math.floor(this.ziel / this.schritt);
    this.auswahl = new Map();
    let gesetzt = 0;
    while (gesetzt < schritte) {
      const z = gefunden[gesetzt % gefunden.length];
      const e = this.auswahl.get(z.id);
      if (e && e.schritte >= 3) break;
      this.auswahl.set(z.id, { zutat: z, schritte: (e ? e.schritte : 0) + 1 });
      gesetzt++;
      if (gesetzt >= schritte) break;
      // Nur einmal reihum, wenn weniger Sorten als Schritte gemerkt sind
      if (gefunden.length >= schritte && gesetzt >= gefunden.length) break;
    }

    this.gruppeAktiv = SaidiBox.AUSWAHL;
    this.aktualisiereKarten();
    this.aktualisiereLeiste();
    this.aktualisiereAuswahlKnopf();
    if (this.el.filter) {
      this.el.filter.querySelectorAll('.saidi-chip').forEach((c) =>
        c.classList.toggle('saidi-chip--an', c.dataset.gruppe === SaidiBox.AUSWAHL));
    }
    this.zeichneListe();

    const hinweis = this.el.zufallHinweis;
    if (hinweis) {
      hinweis.hidden = false;
      hinweis.className = 'saidi-zufall__hinweis';
      // Der Hinweis nennt beide Verluste getrennt: Sorten, die es gar nicht
      // als Portion gibt, und Sorten, die nicht mehr in die Box passen.
      const verwendet = this.auswahl.size;
      const teile = [];
      if (gefunden.length < handles.length) {
        teile.push(`${gefunden.length} von ${handles.length} gemerkten Sorten gibt es als `
          + `${SaidiBox.gewicht(this.schritt)}-Portion – die übrigen sind nicht box-fähig.`);
      }
      if (vergroessertAuf) {
        teile.push(`Box auf ${SaidiBox.gewicht(vergroessertAuf)} gesetzt, damit alle `
          + `${gefunden.length} Sorten hineinpassen.`);
      }
      if (verwendet < gefunden.length) {
        teile.push(`Davon passen ${verwendet} in die ${SaidiBox.gewicht(this.ziel)}-Box – `
          + 'für die übrigen wähle oben eine größere Box.');
      }
      if (!teile.length) {
        teile.push('Aus deinem Merkzettel übernommen. Du kannst alles unten noch ändern.');
      }
      hinweis.textContent = teile.join(' ');
    }
  }

  /**
   * Oeffnet die Warenkorb-Schublade rechts.
   *
   * Warum nicht ueber die Theme-Einstellung "Warenkorb automatisch oeffnen":
   * die ist bewusst aus, damit ein einzelnes Produkt weiterhin nur die kurze
   * Bestaetigung zeigt. Bei der Box ist das anders - dort landen mehrere
   * Positionen auf einmal, und man will sehen, was drin ist.
   *
   * Das Ereignis bringt die Warenkorb-Komponenten dazu, ihren Abschnitt neu
   * zu rendern; ohne mitgelieferte Abschnitte holen sie ihn selbst. Erst
   * danach wird geoeffnet, sonst ginge die Schublade mit altem Inhalt auf.
   */
  async oeffneWarenkorb() {
    // Die Zahl am Warenkorbsymbol kommt aus dieser Zusage: cart-icon.js liest
    // cart.totalQuantity ?? detail.itemCount ?? 0. Ohne beides setzte es die
    // Zahl auf 0 und blendete sie aus - der Korb war voll, das Symbol leer.
    let korb = null;
    try {
      const wurzelPfad = (window.Shopify && window.Shopify.routes)
        ? window.Shopify.routes.root : '/';
      const antwort = await fetch(wurzelPfad + 'cart.js', {
        headers: { Accept: 'application/json' },
      });
      if (antwort.ok) korb = await antwort.json();
    } catch (e) {
      console.warn('Saidi Box: Warenkorbstand nicht abrufbar.', e);
    }

    const ereignis = new Event('shopify:cart:lines-update', { bubbles: true });
    ereignis.action = 'add';
    ereignis.context = 'saidi-box';
    ereignis.promise = Promise.resolve({
      cart: korb ? { totalQuantity: korb.item_count } : undefined,
      detail: { itemCount: korb ? korb.item_count : undefined, source: 'saidi-box' },
    });
    document.dispatchEvent(ereignis);

    const schublade = document.getElementById('cart-drawer');
    if (!schublade || typeof schublade.open !== 'function') return;
    window.setTimeout(() => {
      try {
        if (!schublade.isOpen) schublade.open();
      } catch (e) {
        // Kein Grund, den Nutzer zu behelligen - die Ware liegt im Korb.
        console.warn('Saidi Box: Warenkorb liess sich nicht oeffnen.', e);
      }
    }, 120);
  }

  /* ------------------------------------------------- Zutaten nachladen */

  /*
    Warum es diesen Weg ueberhaupt gibt:

    Liquid gibt in Sektionen nur 50 Produkte einer Kollektion heraus, und
    die Umgehung ueber paginate ist mehrfach still gescheitert - die Box
    stand leer da, ohne Fehlermeldung. Der oeffentliche Endpunkt
    /collections/<handle>/products.json kennt diese Grenze nicht und
    liefert Preis und Bestand tagesaktuell.

    Was ihm fehlt, sind Metafelder. Die Warengruppe kommt deshalb aus
    assets/saidi-box-gruppen.json, erzeugt von 55_box_gruppen.py. Faellt
    diese Datei aus, dient der Produkttyp als Ersatz - dann stimmen die
    Filter nicht perfekt, aber die Box bleibt benutzbar.
  */
  async nachladen() {
    if (this.nachgeladen) return;
    this.nachgeladen = true;

    const handle = this.wurzel.dataset.koll;
    if (!handle) return;
    const wurzelPfad = (window.Shopify && window.Shopify.routes)
      ? window.Shopify.routes.root : '/';

    try {
      const gruppenUrl = this.wurzel.dataset.gruppen;
      const gruppenLauf = gruppenUrl
        ? fetch(gruppenUrl).then((a) => (a.ok ? a.json() : {})).catch(() => ({}))
        : Promise.resolve({});

      // Seitenweise, damit der Endpunkt nicht stillschweigend abschneidet.
      const produkte = [];
      for (let seite = 1; seite <= 6; seite += 1) {
        const antwort = await fetch(
          wurzelPfad + 'collections/' + handle + '/products.json?limit=250&page=' + seite
        );
        if (!antwort.ok) break;
        const daten = await antwort.json();
        const teil = daten.products || [];
        produkte.push(...teil);
        if (teil.length < 250) break;
      }

      const gruppen = await gruppenLauf;
      const liste = [];
      produkte.forEach((p) => {
        const v = (p.variants || []).find(
          (x) => x.available && (x.grams === 100 || x.title === '100gr')
        );
        if (!v) return;
        const zusatz = gruppen[p.handle] || {};
        liste.push({
          id: v.id,
          titel: p.title,
          handle: p.handle,
          preis: Math.round(parseFloat(v.price) * 100),
          gruppe: zusatz.g || p.product_type || '',
          herkunft: zusatz.h || '',
          bild: (p.images && p.images[0]) ? p.images[0].src : '',
        });
      });

      if (!liste.length) {
        console.error('Saidi Box: Kollektion "' + handle + '" liefert keine '
          + 'Zutat mit verfuegbarer 100-g-Portion.');
        return;
      }

      this.zutaten = liste;
      this.el.filter.querySelectorAll('.saidi-chip[data-gruppe]')
        .forEach((c) => c.remove());
      this.baueFilter();
      this.setzeBudgets();
      // Immer neu zeichnen: bis hierhin stand die Liste auf einer Gruppe,
      // die es in den nachgeladenen Daten vielleicht gar nicht mehr gibt.
      this.zeichneListe();
      // Die gemerkte Sorte kann in den ersten 50 gefehlt haben.
      this.vorauswahlAusAdresse();
    } catch (e) {
      console.error('Saidi Box: Zutaten liessen sich nicht laden.', e);
    }
  }

  /**
   * Setzt die Boxgroesse.
   *
   * @param {number} gramm   Zielgewicht
   * @param {boolean} fertig true, sobald der Nutzer den Regler loslaesst -
   *                         erst dann wird gescrollt und neu gemischt.
   */
  setzeGroesse(gramm, fertig) {
    if (!gramm) return;
    this.ziel = gramm;
    if (this.el.regler && this.el.regler.value !== String(gramm)) {
      this.el.regler.value = String(gramm);
    }
    if (this.el.groesseWert) {
      this.el.groesseWert.textContent = SaidiBox.gewicht(gramm);
    }
    this.el.spruenge.forEach((b) => {
      b.classList.toggle('saidi-chip--an', parseInt(b.dataset.gramm, 10) === gramm);
    });

    // Auswahl kuerzen, falls die Box kleiner geworden ist
    while (this.istGewicht > this.ziel && this.auswahl.size) {
      const letzter = [...this.auswahl.keys()].pop();
      const e = this.auswahl.get(letzter);
      if (e.schritte > 1) e.schritte -= 1; else this.auswahl.delete(letzter);
    }

    if (this.el.stufeZufall) {
      this.el.stufeZufall.hidden = false;
      this.el.stufeZufall.classList.remove('saidi-box__stufe--aus');
    }
    this.el.stufeZutaten.hidden = false;
    this.el.stufeZutaten.classList.remove('saidi-box__stufe--aus');
    this.el.leiste.hidden = false;
    if (!this.el.liste.children.length) this.zeichneListe();

    this.setzeBudgets();
    this.aktualisiereKarten();
    this.aktualisiereLeiste();
  }

  /**
   * Beschriftet die Budget-Stufen passend zur Boxgroesse.
   *
   * Feste Betraege waren der Fehler: bei einem Median von 3,50 EUR je 100 g
   * kostet 1 kg rund 35 EUR. "bis 15 EUR" konnte dort nie aufgehen, und der
   * Vorschlag musste jedes Mal melden, dass die Box nicht voll wird - was wie
   * ein Rechenfehler aussah, aber eine unmoegliche Vorgabe war.
   *
   * Untergrenze ist deshalb der tatsaechlich guenstigste Weg, die Box zu
   * fuellen: die billigsten Portionen, hoechstens drei je Zutat - dieselbe
   * Regel, nach der auch gewuerfelt wird.
   */
  setzeBudgets() {
    if (!this.el.budgetStufen.length || !this.zutaten.length || !this.ziel) return;

    const schritte = Math.floor(this.ziel / this.schritt);
    const preise = this.zutaten.map((z) => z.preis).sort((a, b) => a - b);

    let minimum = 0;
    for (let i = 0; i < schritte; i++) {
      // hoechstens drei Portionen je Zutat -> alle drei Schritte die naechste
      minimum += preise[Math.min(Math.floor(i / 3), preise.length - 1)];
    }

    const aufVolleEuro = (cent) => Math.ceil(cent / 100);
    const stufen = [
      aufVolleEuro(minimum * 1.05),
      aufVolleEuro(minimum * 1.45),
      aufVolleEuro(minimum * 2.1),
    ];

    this.el.budgetStufen.forEach((knopf, i) => {
      const wert = stufen[i];
      knopf.dataset.budget = String(wert);
      knopf.textContent = 'bis ' + wert + ' €';
      knopf.hidden = false;
    });
  }

  /* --------------------------------------------------- Überrasch mich */

  /** Fisher-Yates: mischt an Ort und Stelle und gibt die Liste zurueck. */
  static mische(liste) {
    for (let i = liste.length - 1; i > 0; i--) {
      const j = Math.floor(Math.random() * (i + 1));
      [liste[i], liste[j]] = [liste[j], liste[i]];
    }
    return liste;
  }

  static GESCHMACK = {
    nuesse:   ['Pistazien','Cashewkerne','Mandeln','Walnüsse','Haselnüsse','Paranüsse','Erdnüsse'],
    fruechte: ['Datteln','Rosinen','Aprikosen','Feigen','Maulbeeren'],
    suess:    ['Schokoliertes','Datteln','Maulbeeren'],
    knabber:  ['Kichererbsen','Erdnüsse','Saaten & Kerne','Cashewkerne'],
    alles:    null,
  };

  /**
   * Stellt eine Box automatisch zusammen.
   *
   * Regeln, damit das Ergebnis sinnvoll wirkt:
   *  - moeglichst viele verschiedene Warengruppen, nicht fünfmal Pistazien
   *  - Budget wird eingehalten, indem teure Zutaten zuerst aussortiert werden
   *  - immer genau die gewaehlte Boxgroesse, kein Rest
   */
  wuerfle(magKey, budgetEuro) {
    const gruppen = SaidiBox.GESCHMACK[magKey];
    let topf = gruppen
      ? this.zutaten.filter((z) => gruppen.includes(z.gruppe))
      : this.zutaten.slice();

    if (!topf.length) topf = this.zutaten.slice();

    const schritte = Math.floor(this.ziel / this.schritt);
    const budgetCent = budgetEuro > 0 ? budgetEuro * 100 : Infinity;
    // Billigste Portion im Topf - Grundlage fuer die Einteilung unten.
    const guenstigster = topf.reduce((m, z) => Math.min(m, z.preis), Infinity);

    // Bei knappem Budget teure Zutaten von vornherein aussortieren
    if (budgetCent !== Infinity) {
      const proSchritt = budgetCent / schritte;
      const bezahlbar = topf.filter((z) => z.preis <= proSchritt * 1.6);
      if (bezahlbar.length >= 4) topf = bezahlbar;
    }

    // Nach Warengruppe bündeln, damit wir abwechselnd ziehen können
    const nachGruppe = new Map();
    topf.forEach((z) => {
      const g = z.gruppe || 'sonstige';
      if (!nachGruppe.has(g)) nachGruppe.set(g, []);
      nachGruppe.get(g).push(z);
    });
    // sort(() => Math.random() - 0.5) ist kein echtes Mischen - der
    // Vergleich ist nicht konsistent, und die vorderen Plaetze bleiben
    // bevorzugt. Fisher-Yates mischt gleichverteilt.
    nachGruppe.forEach((liste) => SaidiBox.mische(liste));

    const reihenfolge = SaidiBox.mische([...nachGruppe.keys()]);
    const auswahl = new Map();
    let summe = 0;
    let runde = 0;

    // Reihum aus jeder Gruppe eine Zutat, bis die Box voll ist
    for (let i = 0; i < schritte; i++) {
      /*
        Budget bis zum letzten Schritt einteilen.

        Vorher galt nur "passt es noch ins Budget?". Damit gab der Vorschlag
        das Geld gierig zu Beginn aus: bei 9 EUR Deckel lagen nach vier
        Portionen 8,90 EUR in der Box, fuer die fuenfte blieben zehn Cent,
        und die Box blieb bei 400 von 500 g stehen. Fuer den Kunden sah das
        aus, als koenne der Vorschlag nicht rechnen.

        Jetzt wird zurueckgelegt, was die uebrigen Schritte mindestens
        kosten. Was danach frei ist, darf diese Portion kosten - und die Box
        wird voll, solange das Budget ueberhaupt reicht.
      */
      const restNachher = schritte - i - 1;
      const obergrenze = budgetCent === Infinity
        ? Infinity
        : budgetCent - summe - restNachher * guenstigster;

      let gesetzt = false;
      for (let versuch = 0; versuch < reihenfolge.length && !gesetzt; versuch++) {
        const gruppe = reihenfolge[(runde + versuch) % reihenfolge.length];
        const liste = nachGruppe.get(gruppe);
        // Frueher stand hier ein Rueckfall auf liste[0]. Der hat beide
        // Regeln ausgehebelt: dieselbe Zutat konnte fuenfmal in der Box
        // landen, und im ersten Schritt wurde das Budget uebersprungen.
        // Findet sich in dieser Gruppe nichts Passendes, ist die naechste
        // Gruppe dran - das ist der ehrlichere Weg.
        const kandidat = liste.find((z) => {
          const eintrag = auswahl.get(z.id);
          if (eintrag && eintrag.schritte >= 3) return false;   // nicht zu einseitig
          return z.preis <= obergrenze;
        });
        if (!kandidat) continue;
        const vorhanden = auswahl.get(kandidat.id);
        auswahl.set(kandidat.id, {
          zutat: kandidat,
          schritte: (vorhanden ? vorhanden.schritte : 0) + 1,
        });
        summe += kandidat.preis;
        gesetzt = true;
        runde++;
      }
      if (!gesetzt) break;   // Budget reicht nicht weiter
    }
    return { auswahl, summe, schritte };
  }

  vorschlagen() {
    const magKnopf = this.wurzel.querySelector('[data-mag].saidi-chip--an');
    const budgetKnopf = this.wurzel.querySelector('[data-budget].saidi-chip--an');
    const mag = magKnopf ? magKnopf.dataset.mag : 'alles';
    const budget = budgetKnopf ? parseInt(budgetKnopf.dataset.budget, 10) : 0;

    const { auswahl, schritte } = this.wuerfle(mag, budget);
    this.auswahl = auswahl;

    const gefuellt = [...auswahl.values()].reduce((s, e) => s + e.schritte, 0);
    const hinweis = this.el.zufallHinweis;
    hinweis.hidden = false;
    if (gefuellt < schritte) {
      hinweis.textContent =
        'Mit diesem Budget bekommen wir die Box nicht ganz voll. '
        + 'Wähle ein höheres Budget oder ergänze selbst.';
      hinweis.className = 'saidi-zufall__hinweis saidi-zufall__hinweis--warnung';
    } else {
      hinweis.textContent = 'Fertig. Du kannst alles unten noch ändern.';
      hinweis.className = 'saidi-zufall__hinweis';
    }

    this.aktualisiereKarten();
    this.aktualisiereLeiste();

    /*
      Nach dem Wuerfeln direkt auf den Vorschlag springen.

      Vorher landete man in der zuletzt gewaehlten Warengruppe - etwa
      "Aprikosen" - waehrend der Vorschlag quer ueber alle Gruppen verteilt
      lag. Man sah also genau das nicht, was gerade zusammengestellt wurde.
    */
    this.gruppeAktiv = SaidiBox.AUSWAHL;
    this.aktualisiereAuswahlKnopf();
    this.el.filter.querySelectorAll('.saidi-chip').forEach((c) =>
      c.classList.toggle('saidi-chip--an', c.dataset.gruppe === SaidiBox.AUSWAHL));
    this.el.filter.scrollLeft = 0;
    this.zeichneListe();

    this.el.stufeZutaten.scrollIntoView({ behavior: 'smooth', block: 'start' });
  }

  /* ---------------------------------------------------------------- Hilfen */

  static geld(cent) {
    return (cent / 100).toLocaleString('de-DE', {
      style: 'currency', currency: 'EUR',
    });
  }

  static gewicht(gramm) {
    return gramm >= 1000
      ? (gramm / 1000).toLocaleString('de-DE', { maximumFractionDigits: 2 }) + ' kg'
      : gramm + ' g';
  }

  get istGewicht() {
    let g = 0;
    this.auswahl.forEach((e) => { g += e.schritte * this.schritt; });
    return g;
  }

  get istPreis() {
    let c = 0;
    this.auswahl.forEach((e) => { c += e.schritte * e.zutat.preis; });
    return c;
  }

  get restSchritte() {
    return Math.max(0, Math.floor((this.ziel - this.istGewicht) / this.schritt));
  }

  /* --------------------------------------------------------------- Aufbau */

  /**
   * Kennung der Sammelgruppe "Deine Auswahl".
   *
   * Seit es kein "Alle" mehr gibt, zeigt die Liste immer nur eine
   * Warengruppe. Nach "Box vorschlagen" lagen die gewuerfelten Zutaten aber
   * quer ueber alle Gruppen verteilt - man sah von seinem eigenen Vorschlag
   * nichts. Diese Gruppe sammelt genau das, was in der Box liegt.
   */
  static AUSWAHL = '__auswahl';

  /** Setzt oder entfernt den Knopf "Deine Auswahl" je nach Inhalt der Box. */
  aktualisiereAuswahlKnopf() {
    if (!this.el.filter) return;
    const anzahl = this.auswahl.size;
    let knopf = this.el.filter.querySelector(`[data-gruppe="${SaidiBox.AUSWAHL}"]`);

    if (!anzahl) {
      if (knopf) {
        const warAn = knopf.classList.contains('saidi-chip--an');
        knopf.remove();
        // Stand die Liste auf der Auswahl, muss sie jetzt woandershin.
        if (warAn) {
          const erster = this.el.filter.querySelector('.saidi-chip');
          if (erster) {
            this.gruppeAktiv = erster.dataset.gruppe;
            erster.classList.add('saidi-chip--an');
            this.zeichneListe();
          }
        }
      }
      return;
    }

    if (!knopf) {
      knopf = document.createElement('button');
      knopf.type = 'button';
      knopf.className = 'saidi-chip saidi-chip--auswahl';
      knopf.dataset.gruppe = SaidiBox.AUSWAHL;
      this.el.filter.prepend(knopf);
    }
    knopf.textContent = 'Deine Auswahl (' + anzahl + ')';
  }

  baueFilter() {
    const gruppen = [...new Set(this.zutaten.map((z) => z.gruppe).filter(Boolean))].sort(
      (a, b) => a.localeCompare(b, 'de')
    );
    // Es ist immer genau eine Gruppe gewaehlt - ohne "Alle". 208 Zutaten am
    // Stueck sind keine Uebersicht, sondern eine lange Liste.
    if (!this.gruppeAktiv || !gruppen.includes(this.gruppeAktiv)) {
      this.gruppeAktiv = gruppen[0] || '';
    }
    gruppen.forEach((g) => {
      const b = document.createElement('button');
      b.type = 'button';
      b.className = 'saidi-chip' + (g === this.gruppeAktiv ? ' saidi-chip--an' : '');
      b.dataset.gruppe = g;
      b.textContent = g;
      this.el.filter.appendChild(b);
    });
  }

  zeichneListe(gruppe = this.gruppeAktiv || '') {
    let sichtbar;
    if (gruppe === SaidiBox.AUSWAHL) {
      sichtbar = this.zutaten.filter((z) => this.auswahl.has(z.id));
    } else if (gruppe) {
      sichtbar = this.zutaten.filter((z) => z.gruppe === gruppe);
    } else {
      sichtbar = this.zutaten;
    }

    this.el.leer.hidden = sichtbar.length > 0;
    this.el.liste.innerHTML = '';

    const bruch = document.createDocumentFragment();
    sichtbar.forEach((z) => bruch.appendChild(this.karte(z)));
    this.el.liste.appendChild(bruch);
    this.aktualisiereKarten();
  }

  karte(z) {
    const el = document.createElement('article');
    el.className = 'saidi-zutat';
    el.dataset.id = z.id;

    const bild = z.bild
      ? `<img class="saidi-zutat__bild" src="${z.bild}" alt="" loading="lazy" width="72" height="72">`
      : '<span class="saidi-zutat__bild saidi-zutat__bild--leer"></span>';

    el.innerHTML = `
      ${bild}
      <div class="saidi-zutat__text">
        <h3 class="saidi-zutat__titel"></h3>
        <p class="saidi-zutat__meta">
          <span class="saidi-zutat__preis">${SaidiBox.geld(z.preis)} / ${this.schritt} g</span>
          ${z.herkunft ? `<span class="saidi-zutat__herkunft">${z.herkunft}</span>` : ''}
        </p>
      </div>
      <div class="saidi-zutat__menge">
        <button type="button" class="saidi-zutat__btn" data-minus aria-label="Weniger">&minus;</button>
        <span class="saidi-zutat__wert" data-wert>0 g</span>
        <button type="button" class="saidi-zutat__btn" data-plus aria-label="Mehr">+</button>
      </div>
      <span class="saidi-zutat__haken" data-haken aria-hidden="true">&#10003;</span>`;

    // Titel als Text setzen - Produktnamen kommen aus dem Katalog
    el.querySelector('.saidi-zutat__titel').textContent = z.titel;

    el.querySelector('[data-plus]').addEventListener('click', () => this.aendere(z, +1));
    el.querySelector('[data-minus]').addEventListener('click', () => this.aendere(z, -1));
    return el;
  }

  /* -------------------------------------------------------------- Zustand */

  aendere(zutat, richtung) {
    const vorhanden = this.auswahl.get(zutat.id);
    const schritte = (vorhanden ? vorhanden.schritte : 0) + richtung;

    if (schritte <= 0) {
      this.auswahl.delete(zutat.id);
    } else {
      if (richtung > 0 && this.restSchritte <= 0) {
        this.blinke('Die Box ist voll. Nimm zuerst etwas heraus.');
        return;
      }
      this.auswahl.set(zutat.id, { zutat, schritte });
    }
    this.aktualisiereKarten();
    this.aktualisiereLeiste();
  }

  aktualisiereKarten() {
    const z_titel = (el) => {
      const t = el.querySelector('.saidi-zutat__titel');
      return t ? t.textContent : '';
    };
    this.el.liste.querySelectorAll('.saidi-zutat').forEach((el) => {
      const eintrag = this.auswahl.get(Number(el.dataset.id));
      const gramm = eintrag ? eintrag.schritte * this.schritt : 0;
      el.querySelector('[data-wert]').textContent = gramm ? SaidiBox.gewicht(gramm) : '0 g';
      el.classList.toggle('saidi-zutat--drin', gramm > 0);
      // Vorlesbar machen: die Farbe allein sagt nichts, wer sie nicht sieht.
      el.setAttribute('aria-label', z_titel(el) + (gramm
        ? ', ' + SaidiBox.gewicht(gramm) + ' in der Box'
        : ', nicht in der Box'));
      el.querySelector('[data-minus]').disabled = gramm === 0;
      el.querySelector('[data-plus]').disabled = this.restSchritte <= 0;
    });
    this.aktualisiereAuswahlKnopf();
  }

  /* Ist die Verpackung angehakt? Eigener Getter, weil an drei Stellen
     gebraucht: Anzeige, Warenkorb und Zuruecksetzen. */
  get istGeschenk() {
    return !!(this.el.geschenkAn && this.el.geschenkAn.checked && this.geschenkVariante);
  }

  aktualisiereLeiste() {
    const ist = this.istGewicht;
    const anteil = this.ziel ? Math.min(100, (ist / this.ziel) * 100) : 0;
    const voll = ist === this.ziel && ist > 0;

    this.el.ist.textContent = SaidiBox.gewicht(ist);
    this.el.zielAnzeige.textContent = '/ ' + SaidiBox.gewicht(this.ziel);
    // Der angezeigte Betrag muss dem entsprechen, was gleich abgebucht
    // wird - sonst wirkt die Verpackung im Warenkorb wie ein Aufschlag aus
    // dem Nichts.
    const aufschlag = this.istGeschenk ? this.geschenkPreis : 0;
    this.el.preis.textContent = SaidiBox.geld(this.istPreis + aufschlag);
    this.el.fuell.style.width = anteil + '%';
    this.el.fuell.classList.toggle('saidi-box__balken-fuell--voll', voll);

    if (voll) {
      this.el.status.textContent = 'Deine Saidi Box ist fertig';
      this.el.status.classList.add('saidi-box__status--fertig');
    } else {
      const fehlt = this.ziel - ist;
      this.el.status.textContent = ist === 0
        ? 'Wähle deine Zutaten'
        : 'Noch ' + SaidiBox.gewicht(fehlt) + ' bis zur vollen Box';
      this.el.status.classList.remove('saidi-box__status--fertig');
    }
    this.el.absenden.disabled = !voll;
  }

  blinke(text) {
    this.el.status.textContent = text;
    this.el.status.classList.add('saidi-box__status--warnung');
    window.clearTimeout(this._blinkZeit);
    this._blinkZeit = window.setTimeout(() => {
      this.el.status.classList.remove('saidi-box__status--warnung');
      this.aktualisiereLeiste();
    }, 1800);
  }

  zaehleZeichen() {
    if (this.el.zeichen && this.el.nachricht) {
      this.el.zeichen.textContent = this.el.nachricht.value.length;
    }
  }

  /* ------------------------------------------------------------ Warenkorb */

  async absenden() {
    const knopf = this.el.absenden;
    knopf.disabled = true;
    knopf.classList.add('saidi-box__knopf--laedt');

    const boxId = 'BOX-' + Date.now().toString(36).toUpperCase();
    const groesse = SaidiBox.gewicht(this.ziel);
    const gesamt = SaidiBox.geld(this.istPreis);

    const alsGeschenk = this.istGeschenk;

    const positionen = [];
    this.auswahl.forEach(({ zutat, schritte }) => {
      positionen.push({
        id: zutat.id,
        quantity: schritte,
        properties: {
          // Unterstrich davor: Shopify blendet solche Eigenschaften im
          // Warenkorb und an der Kasse aus, behaelt sie aber in der
          // Bestellung. Sichtbar stand hier "Teil von: Meine Saidi Box
          // (500 g)" an jeder einzelnen Zutat - dasselbe, was schon in der
          // Ueberschrift der Gruppe steht. Fuers Packen bleibt die Angabe
          // im Adminbereich erhalten.
          '_Teil von': (alsGeschenk ? 'Meine Saidi Box, verpackt (' : 'Meine Saidi Box (')
            + groesse + ')',
          'Menge in der Box': SaidiBox.gewicht(schritte * this.schritt),
          '_box_id': boxId,
          '_box_groesse_g': String(this.ziel),
          '_box_gesamtpreis': gesamt,
        },
      });
    });

    // Anlass und Nachricht gehoeren an die Verpackung, nicht an eine
    // zufaellig erste Zutat. Wer im Lager packt, liest sie damit genau
    // dort, wo die Karte geschrieben wird.
    if (alsGeschenk) {
      const anlassKnopf = this.wurzel.querySelector('[data-anlass].saidi-chip--an');
      const eigenschaften = {
        '_Gehört zu': 'Meine Saidi Box (' + groesse + ')',
        '_box_id': boxId,
      };
      if (anlassKnopf) eigenschaften['Anlass'] = anlassKnopf.dataset.anlass;
      if (this.el.nachricht && this.el.nachricht.value.trim()) {
        eigenschaften['Nachricht'] = this.el.nachricht.value.trim();
      }
      if (this.el.von && this.el.von.value.trim()) {
        eigenschaften['Von'] = this.el.von.value.trim();
      }
      positionen.push({ id: this.geschenkVariante, quantity: 1, properties: eigenschaften });
    }

    try {
      // Die Kurzmeldung aus saidi-warenkorb-hinweis.liquid wuerde hier je
      // Zutat einmal erscheinen. Bei der Box uebernimmt die Schublade.
      window.saidiKeinHinweis = true;
      const antwort = await fetch(window.Shopify.routes.root + 'cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items: positionen }),
      });
      if (!antwort.ok) throw new Error('cart');
      this.auswahl.clear();
      if (this.el.nachricht) { this.el.nachricht.value = ''; this.zaehleZeichen(); }
      if (this.el.von) this.el.von.value = '';
      if (this.el.geschenkAn) {
        this.el.geschenkAn.checked = false;
        if (this.el.geschenkDetails) this.el.geschenkDetails.hidden = true;
      }
      this.wurzel.querySelectorAll('[data-anlass]')
        .forEach((c) => c.classList.remove('saidi-chip--an'));
      this.aktualisiereKarten();
      this.aktualisiereLeiste();
      this.el.status.textContent = 'Box im Warenkorb. Du kannst gleich die nächste bauen.';
      this.el.status.classList.add('saidi-box__status--fertig');
      this.oeffneWarenkorb();
    } catch (e) {
      this.blinke('Das hat nicht geklappt. Bitte versuch es noch einmal.');
    } finally {
      window.saidiKeinHinweis = false;
      knopf.classList.remove('saidi-box__knopf--laedt');
      this.aktualisiereLeiste();
    }
  }

  /* --------------------------------------------------------------- Bindung */

  verdrahte() {
    if (this.el.regler) {
      // 'input' statt 'change': die Anzeige soll beim Ziehen mitlaufen.
      this.el.regler.addEventListener('input', () => {
        this.setzeGroesse(parseInt(this.el.regler.value, 10) || 0, false);
      });
      this.el.regler.addEventListener('change', () => {
        this.setzeGroesse(parseInt(this.el.regler.value, 10) || 0, true);
      });
    }

    this.el.spruenge.forEach((b) => {
      b.addEventListener('click', () => {
        this.setzeGroesse(parseInt(b.dataset.gramm, 10) || 0, true);
      });
    });

    this.el.filter.addEventListener('click', (e) => {
      const chip = e.target.closest('.saidi-chip');
      if (!chip) return;
      this.gruppeAktiv = chip.dataset.gruppe;
      this.el.filter.querySelectorAll('.saidi-chip').forEach((c) =>
        c.classList.toggle('saidi-chip--an', c === chip));

      // Die Gruppenliste ist eine Wischreihe. Wer weit rechts waehlt, soll
      // seine Wahl danach noch sehen - sonst scrollt die Reihe beim naechsten
      // Aufbau zurueck an den Anfang und man weiss nicht mehr, wo man ist.
      chip.scrollIntoView({ inline: 'center', block: 'nearest', behavior: 'smooth' });

      this.zeichneListe(chip.dataset.gruppe);
    });

    this.el.absenden.addEventListener('click', () => this.absenden());

    // Überrasch mich
    this.el.zufallWahl.forEach((gruppe) => {
      gruppe.addEventListener('click', (e) => {
        const chip = e.target.closest('.saidi-chip');
        if (!chip) return;
        gruppe.querySelectorAll('.saidi-chip').forEach((c) =>
          c.classList.toggle('saidi-chip--an', c === chip));
      });
    });
    if (this.el.wuerfeln) {
      this.el.wuerfeln.addEventListener('click', () => this.vorschlagen());
    }

    // Geschenk: Anlass ist abwaehlbar - jemand kann die Box auch ohne Anlass
    // verschenken, ein erzwungener Pflichtwert waere gaengelnd.
    if (this.el.anlaesse) {
      this.el.anlaesse.addEventListener('click', (e) => {
        const chip = e.target.closest('.saidi-chip');
        if (!chip) return;
        const war = chip.classList.contains('saidi-chip--an');
        this.el.anlaesse.querySelectorAll('.saidi-chip')
          .forEach((c) => c.classList.remove('saidi-chip--an'));
        if (!war) chip.classList.add('saidi-chip--an');
      });
    }
    if (this.el.nachricht) {
      this.el.nachricht.addEventListener('input', () => this.zaehleZeichen());
    }

    if (this.el.geschenkAn) {
      this.el.geschenkAn.addEventListener('change', () => {
        if (this.el.geschenkDetails) {
          this.el.geschenkDetails.hidden = !this.el.geschenkAn.checked;
        }
        this.aktualisiereLeiste();
      });
    }
  }
}

function saidiBoxStarten(bereich) {
  (bereich || document).querySelectorAll('.saidi-box').forEach((el) => {
    if (el.dataset.saidiBereit) return;
    el.dataset.saidiBereit = '1';
    new SaidiBox(el);
  });
}

saidiBoxStarten();

// Der Theme-Editor rendert Sektionen neu, ohne die Seite zu laden.
document.addEventListener('shopify:section:load', (e) => saidiBoxStarten(e.target));
